import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useKadryStaff } from '@entities/kadry';

const Id = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1)};
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  overflow-wrap: anywhere;
`;

const Copy = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font: inherit;
  font-family: ${({ theme }) => theme.font.family};
  font-size: 11px;
  line-height: 1.4;
  padding: 0 6px;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

interface Props {
  /** UUID сотрудника. */
  id: string | null | undefined;
  /** Имя, если оно уже известно (например, `actor_name` журнала). */
  name?: string | null;
  /** Подпись при отсутствии `id`. */
  empty?: string;
}

/**
 * Сотрудник по UUID: ФИО из ответа сервера или справочника, иначе — полный
 * UUID с кнопкой копирования (чтобы найти человека в журнале или у
 * администратора, не гадая по восьми символам).
 */
export function KadryPerson({ id, name, empty = '—' }: Props) {
  const staff = useKadryStaff();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(t);
  }, [copied]);

  if (!id) return <>{name ?? empty}</>;
  const known = name ?? staff.nameOf(id);
  if (known) return <>{known}</>;

  async function copy() {
    try {
      await navigator.clipboard.writeText(id as string);
      setCopied(true);
    } catch {
      // Буфер обмена недоступен (не HTTPS) — UUID и так виден целиком.
    }
  }

  return (
    <Id title="Сотрудник не найден в доступном вам справочнике">
      {id}
      <Copy type="button" onClick={copy} aria-label="Скопировать идентификатор сотрудника">
        {copied ? 'скопировано' : 'копировать'}
      </Copy>
    </Id>
  );
}
